<!-- eslint-disable no-undef -->
<script setup>
import { ref, onMounted } from 'vue';
import { useAppManagement } from '@/composables/useAppManagement';
import AppConfigModal from './AppConfigModal.vue';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';

const props = defineProps({ deviceId: { type: String, required: true } });

const {
    apps, loading, error, actionLoading,
    appActions,
    fetchApps, fetchAppActions, sendAppAction, sendAction, awaitResult,
} = useAppManagement(props.deviceId);

const configModalApp = ref(null);
const openMenuId = ref(null);
const rowErrors = ref({});
const logsModal = ref(null);      // { app, content: string }
const manifestModal = ref(null);  // { app, content: object }
const manifestLoading = ref(null); // app while fetching

onMounted(() => {
    fetchApps();
    fetchAppActions();
});

const openConfigModal = (app) => {
    configModalApp.value = app;
    openMenuId.value = null;
};

const closeConfigModal = () => { configModalApp.value = null; };

const toggleMenu = (appId) => {
    openMenuId.value = openMenuId.value === appId ? null : appId;
};

// Used for Execute actions that should re-fetch the app list after completing.
const handleAction = async (appId, action) => {
    rowErrors.value = { ...rowErrors.value, [appId]: null };
    try {
        await sendAppAction(appId, action);
        await fetchApps();
    } catch (e) {
        rowErrors.value = { ...rowErrors.value, [appId]: e.message || 'Action failed' };
    }
};

const handleViewLogs = async (app) => {
    openMenuId.value = null;
    try {
        const result = await sendAppAction(app.app_id, 'status', 'Read');
        logsModal.value = {
            app,
            content: result?.logs || result?.status_output || 'No log output available.',
        };
    } catch (e) {
        rowErrors.value = { ...rowErrors.value, [app.app_id]: e.message || 'Failed to fetch logs' };
    }
};

const handleViewManifest = async (app) => {
    openMenuId.value = null;
    manifestLoading.value = app;
    try {
        const result = await sendAppAction(app.app_id, 'manifest', 'Read');
        manifestModal.value = { app, content: result };
    } catch (e) {
        rowErrors.value = { ...rowErrors.value, [app.app_id]: e.message || 'Failed to fetch manifest' };
    } finally {
        manifestLoading.value = null;
    }
};

const handleTemplateAction = async (app, actionDef) => {
    if (actionDef.action === 'status') {
        await handleViewLogs(app);
    } else if (actionDef.actionType === 'Execute') {
        await handleAction(app.app_id, actionDef.action);
    }
};

const stateBadgeClass = (state) => {
    if (state === 'active')   return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (state === 'failed')   return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    return 'bg-mc-steel/30 text-ink-muted dark:bg-white/10 dark:text-on-dark-muted';
};

const stateDotClass = (state) => {
    if (state === 'active') return 'bg-green-500 dark:bg-green-400';
    if (state === 'failed') return 'bg-mc-red';
    return 'bg-ink-muted dark:bg-on-dark-muted';
};

const isLoading = (appId, action) => !!(actionLoading.value && actionLoading.value[`${appId}:${action}`]);

defineExpose({ openConfigModal, handleViewLogs, handleViewManifest, handleTemplateAction });
</script>

<template>
    <div>
        <!-- Header -->
        <div class="flex justify-between items-center mb-4">
            <div>
                <h2 class="font-display font-bold text-tier-2 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">
                    Installed Apps
                </h2>
                <p class="font-body text-tier-1 text-ink-muted dark:text-on-dark-muted mt-0.5">Queried live from device</p>
            </div>
            <button
                type="button"
                class="px-3 py-1.5 text-sm font-semibold rounded-mc-sm border border-mc-steel dark:border-line-on-dark text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                @click="fetchApps"
            >↻ Refresh</button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-12 gap-3">
            <LoadingAnimation3 size="36px" />
            <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Contacting unit for installed apps…</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col items-center gap-3 py-12">
            <p class="font-body text-tier-3 text-mc-red">{{ error }}</p>
            <button type="button" class="btn-primary text-sm" @click="fetchApps">Retry</button>
        </div>

        <!-- Empty -->
        <div v-else-if="apps.length === 0" class="flex items-center justify-center py-12">
            <p class="font-body text-tier-3 text-ink-muted dark:text-on-dark-muted">No apps installed on this device.</p>
        </div>

        <!-- App table -->
        <div v-else class="border border-mc-steel dark:border-line-on-dark rounded-mc-md">
            <!-- Table header -->
            <div class="flex items-center gap-4 px-4 py-2 bg-mc-steel/20 dark:bg-white/5 font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted rounded-t-mc-md">
                <span class="flex-1 min-w-0">Application</span>
                <span class="w-40 shrink-0 whitespace-nowrap">Version</span>
                <span class="w-32 shrink-0 whitespace-nowrap">State</span>
                <span class="w-28 shrink-0 whitespace-nowrap">Boot</span>
                <span class="w-24 shrink-0"></span>
            </div>

            <!-- App rows -->
            <div
                v-for="app in apps"
                :key="app.app_id"
                class="flex items-center gap-4 px-4 py-3 border-t border-mc-steel dark:border-line-on-dark last:rounded-b-mc-md"
                :class="app.state === 'failed' ? 'bg-red-50 dark:bg-red-900/10' : ''"
            >
                <!-- App name + id + row error -->
                <div class="flex-1 min-w-0">
                    <div class="font-body font-semibold text-tier-3 text-mc-ink dark:text-on-dark">{{ app.name }}</div>
                    <div class="font-mono text-tier-1 text-ink-muted dark:text-on-dark-muted">{{ app.app_id }}</div>
                    <div v-if="rowErrors[app.app_id]" class="text-xs text-mc-red mt-0.5">{{ rowErrors[app.app_id] }}</div>
                </div>

                <!-- Version -->
                <div class="w-40 shrink-0 font-body text-tier-3 text-mc-ink dark:text-on-dark">{{ app.version }}</div>

                <!-- State badge -->
                <div class="w-32 shrink-0">
                    <span
                        class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                        :class="stateBadgeClass(app.state)"
                    >
                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="stateDotClass(app.state)"></span>
                        {{ app.state }}
                    </span>
                </div>

                <!-- Boot status -->
                <div class="w-28 shrink-0 font-body text-tier-2 text-ink-muted dark:text-on-dark-muted">
                    {{ app.enabled ? 'enabled' : 'disabled' }}
                </div>

                <!-- Actions dropdown -->
                <div class="w-24 shrink-0 flex justify-end relative">
                    <button
                        type="button"
                        class="px-2.5 py-1.5 text-xs font-semibold rounded-mc-sm border border-mc-steel dark:border-line-on-dark text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                        :aria-label="`Actions for ${app.name}`"
                        @click.stop="toggleMenu(app.app_id)"
                    >Actions ▾</button>
                    <div
                        v-if="openMenuId === app.app_id"
                        class="absolute right-0 top-full mt-1 w-48 bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm shadow-mc-md z-10"
                        @click.stop
                    >
                        <button
                            v-for="actionDef in appActions"
                            :key="actionDef.action"
                            type="button"
                            class="w-full text-left px-3 py-2 text-sm font-body text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160 disabled:opacity-40 disabled:cursor-not-allowed"
                            :disabled="isLoading(app.app_id, actionDef.action)"
                            :title="actionDef.description"
                            @click="handleTemplateAction(app, actionDef)"
                        >{{ actionDef.name }}</button>
                        <div class="border-t border-mc-steel dark:border-line-on-dark my-1"></div>
                        <button
                            type="button"
                            class="w-full text-left px-3 py-2 text-sm font-body text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160"
                            @click="openConfigModal(app)"
                        >Edit Configuration</button>
                        <button
                            type="button"
                            class="w-full text-left px-3 py-2 text-sm font-body text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160"
                            @click="handleViewManifest(app)"
                        >View Manifest</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Config modal -->
        <AppConfigModal
            v-if="configModalApp"
            :app="configModalApp"
            :deviceId="deviceId"
            :sendAction="sendAction"
            :awaitResult="awaitResult"
            @close="closeConfigModal"
        />

        <!-- Logs modal -->
        <div
            v-if="logsModal"
            class="fixed inset-0 bg-mc-ink/30 flex justify-center items-center z-50"
            @click.self="logsModal = null"
        >
            <div class="bg-mc-white dark:bg-mc-charcoal rounded-mc-md shadow-mc-md w-[600px] max-h-[80vh] flex flex-col">
                <div class="flex justify-between items-center p-4 border-b border-mc-steel dark:border-line-on-dark flex-shrink-0">
                    <h3 class="font-display font-black text-tier-4 text-mc-ink dark:text-on-dark">
                        Logs — {{ logsModal.app.name }}
                    </h3>
                    <button
                        type="button"
                        class="text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark text-2xl leading-none"
                        aria-label="Close"
                        @click="logsModal = null"
                    >×</button>
                </div>
                <pre class="flex-1 overflow-y-auto p-4 font-mono text-xs text-mc-ink dark:text-on-dark whitespace-pre-wrap">{{ logsModal.content }}</pre>
            </div>
        </div>

        <!-- Manifest modal -->
        <div
            v-if="manifestLoading || manifestModal"
            class="fixed inset-0 bg-mc-ink/30 flex justify-center items-center z-50"
            @click.self="manifestModal = null"
        >
            <div class="bg-mc-white dark:bg-mc-charcoal rounded-mc-md shadow-mc-md w-[600px] max-h-[80vh] flex flex-col">
                <div class="flex justify-between items-center p-4 border-b border-mc-steel dark:border-line-on-dark flex-shrink-0">
                    <h3 class="font-display font-black text-tier-4 text-mc-ink dark:text-on-dark">
                        Manifest — {{ manifestModal ? manifestModal.app.name : manifestLoading.name }}
                    </h3>
                    <button
                        v-if="manifestModal"
                        type="button"
                        class="text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark text-2xl leading-none"
                        aria-label="Close"
                        @click="manifestModal = null"
                    >×</button>
                </div>
                <div v-if="manifestLoading" class="flex flex-col items-center justify-center gap-3 p-8">
                    <LoadingAnimation3 size="36px" />
                    <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Contacting unit for manifest…</p>
                </div>
                <pre v-else class="flex-1 overflow-y-auto p-4 font-mono text-xs text-mc-ink dark:text-on-dark whitespace-pre-wrap">{{ JSON.stringify(manifestModal.content, null, 2) }}</pre>
            </div>
        </div>

        <!-- Close menus on outside click -->
        <div
            v-if="openMenuId !== null"
            class="fixed inset-0 z-0"
            @click="openMenuId = null"
        ></div>
    </div>
</template>
